import * as React from "react"
import { HelpCircle, BookOpen, FileText, MessageCircle, Settings, Users, BarChart3, ClipboardList, Search, Link2, Play, Maximize2, PictureInPicture } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import { useHelpContent } from "@/hooks/useHelpContent"
import type { HelpSection, HelpItem, HelpDocument } from "@/assets/help/help-content.types"
import { useContactOverlay } from "./contact-overlay"



interface HelpOverlayContextValue {
  state: "open" | "closed"
  setState: (state: "open" | "closed") => void
}

const HelpOverlayContext = React.createContext<HelpOverlayContextValue | undefined>(undefined)

const useHelpOverlay = () => {
  const context = React.useContext(HelpOverlayContext)
  if (!context) {
    throw new Error("useHelpOverlay must be used within a HelpOverlayProvider")
  }
  return context
}

interface HelpOverlayProviderProps {
  children: React.ReactNode
  defaultState?: "open" | "closed"
}

const HelpOverlayProvider = ({
  children,
  defaultState = "closed",
}: HelpOverlayProviderProps) => {
  const [state, setState] = React.useState<"open" | "closed">(defaultState)

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "h") {
        event.preventDefault()
        setState(prev => prev === "open" ? "closed" : "open")
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  return (
    <HelpOverlayContext.Provider value={{ state, setState }}>
      {children}
    </HelpOverlayContext.Provider>
  )
}

const HelpOverlayTrigger = () => {
  const { state, setState } = useHelpOverlay()

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-18 w-18"
            onClick={() => setState(state === "open" ? "closed" : "open")}
          >
            <HelpCircle className="!h-9 !w-9" />
            <span className="sr-only">Help</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Help</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Video Player Context for managing multiple players
const VideoPlayerContext = React.createContext<{
  stopOtherPlayers: (currentPlayerId: string) => void;
}>({
  stopOtherPlayers: () => {},
})



// Embedded Video Player Component
function EmbeddedVideoPlayer({ 
  videoUrl, 
  onClose,
  isOverlayOpen,
  hasUserSelected
}: { 
  videoUrl: string; 
  onClose: () => void;
  isOverlayOpen: boolean;
  hasUserSelected: boolean;
}) {
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const [isPiP, setIsPiP] = React.useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const playerId = React.useId()
  const { stopOtherPlayers } = React.useContext(VideoPlayerContext)

  const fullVideoUrl = `${import.meta.env.VITE_REACT_MEDIA_WEB_URL}/help/${videoUrl}`

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const togglePictureInPicture = async () => {
    const video = videoRef.current
    if (!video) return

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture()
      } else {
        await video.requestPictureInPicture()
        // Close the help overlay when PiP is activated
        onClose()
      }
    } catch (error) {
      console.error('Picture-in-Picture failed:', error)
    }
  }

  const handlePlay = React.useCallback(() => {
    stopOtherPlayers(playerId)
  }, [stopOtherPlayers, playerId])

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    const handlePiPChange = () => {
      setIsPiP(!!document.pictureInPictureElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('enterpictureinpicture', handlePiPChange)
    document.addEventListener('leavepictureinpicture', handlePiPChange)
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('enterpictureinpicture', handlePiPChange)
      document.removeEventListener('leavepictureinpicture', handlePiPChange)
    }
  }, [])

  React.useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.addEventListener('play', handlePlay)
      return () => video.removeEventListener('play', handlePlay)
    }
  }, [handlePlay])

  // Auto-play when video URL changes (but not on initial load)
  const [isInitialLoad, setIsInitialLoad] = React.useState(true)
  
  // Reset isInitialLoad when overlay opens/closes
  React.useEffect(() => {
    if (isOverlayOpen) {
      setIsInitialLoad(true)
    }
  }, [isOverlayOpen])
  
  React.useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false)
      return
    }
    
    // Only auto-play if user has actively selected a video
    if (!hasUserSelected) {
      return
    }
    
    const video = videoRef.current
    if (video && videoUrl) {
      video.load()
      video.play().catch(error => {
        console.log('Auto-play prevented:', error)
      })
    }
  }, [videoUrl, isInitialLoad, hasUserSelected])

  // Check if PiP is supported
  const isPiPSupported = document.pictureInPictureEnabled

  return (
    <div className="w-full">
      <div className="flex items-center justify-end mb-2">
        <div className="flex items-center gap-1">
          {isPiPSupported && (
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePictureInPicture}
              className="h-6 px-2 text-xs"
              disabled={isPiP}
            >
              <PictureInPicture className="h-3 w-3 mr-1" />
              {isPiP ? 'PiP Active' : 'Picture in Picture (watch and do it at once)'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="h-6 px-2 text-xs"
          >
            <Maximize2 className="h-3 w-3 mr-1" />
            Fullscreen
          </Button>
        </div>
      </div>
      <div 
        ref={containerRef}
        className={`relative bg-black rounded-lg overflow-hidden ${isFullscreen ? 'w-full h-full' : 'w-full'}`}
      >
        <video
          ref={videoRef}
          src={fullVideoUrl}
          controls
          className={`${isFullscreen ? 'w-full h-full object-contain' : 'w-full aspect-video object-contain'}`}
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  )
}



// Help Item Component for Playlist
function HelpItemCard({ 
  item, 
  section, 
  isSelected,
  onSelect
}: { 
  item: HelpItem; 
  section: HelpSection; 
  isSelected: boolean;
  onSelect: () => void;
}) {
  const IconComponent = getIconComponent(item.icon || section.icon)
  const iconColor = item.iconColor || section.iconColor

  return (
    <div 
      className={`border rounded-lg cursor-pointer transition-colors ${
        isSelected 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      onClick={onSelect}
    >
      <div className="p-3">
        <div className="flex items-center gap-2">
          {IconComponent && <IconComponent className={`h-4 w-4 ${iconColor}`} />}
          <h4 className="font-medium flex-1">{item.title}</h4>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
      </div>
    </div>
  )
}

function helpMediaUrl(relativePath: string) {
  const mediaBase = import.meta.env.VITE_REACT_MEDIA_WEB_URL
  const encodedPath = relativePath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/")
  return `${mediaBase}/help/${encodedPath}`
}

function DocumentsNavCard({
  isSelected,
  onSelect,
  count,
}: {
  isSelected: boolean;
  onSelect: () => void;
  count: number;
}) {
  return (
    <div
      className={`border rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      onClick={onSelect}
    >
      <div className="p-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-orange-600" />
          <h4 className="font-medium flex-1">Documents and Forms</h4>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Guides, checklists, and templates ({count})
        </p>
      </div>
    </div>
  )
}

function DocumentsList({ documents }: { documents: HelpDocument[] }) {
  const grouped = React.useMemo(() => {
    const groups = new Map<string, HelpDocument[]>()
    documents.forEach((doc) => {
      const category = doc.category || "Documents"
      const existing = groups.get(category) ?? []
      existing.push(doc)
      groups.set(category, existing)
    })
    return Array.from(groups.entries())
  }, [documents])

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold">Documents and Forms</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Open a file to view or download it. PDF and Word are offered when both are available.
        </p>
      </div>
      {grouped.map(([category, docs]) => (
        <div key={category} className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {category}
          </h4>
          <div className="space-y-2">
            {docs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-start gap-3 border rounded-lg p-3"
              >
                <FileText className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <h5 className="font-medium">{doc.title}</h5>
                  {doc.description && (
                    <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {doc.pdfUrl && (
                    <Button variant="outline" size="sm" className="h-8 px-2 gap-1.5" asChild>
                      <a
                        href={helpMediaUrl(doc.pdfUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open PDF"
                        aria-label={`Open ${doc.title} as PDF`}
                      >
                        <FileText className="h-4 w-4 text-red-600" />
                        <span className="text-xs font-medium">PDF</span>
                      </a>
                    </Button>
                  )}
                  {doc.docxUrl && (
                    <Button variant="outline" size="sm" className="h-8 px-2 gap-1.5" asChild>
                      <a
                        href={helpMediaUrl(doc.docxUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open Word document"
                        aria-label={`Open ${doc.title} as Word`}
                      >
                        <FileText className="h-4 w-4 text-blue-700" />
                        <span className="text-xs font-medium">Word</span>
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Helper function to get icon component
function getIconComponent(iconName: string) {
  const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
    BookOpen,
    FileText,
    MessageCircle,
    Settings,
    Users,
    BarChart3,
    ClipboardList,
    Search,
    Link2,
    HelpCircle
  }
  return iconMap[iconName]
}

type HelpSelection =
  | { type: "documents" }
  | { type: "video"; item: HelpItem; section: HelpSection }

const HelpOverlay = () => {
  const { state, setState } = useHelpOverlay()
  const { openWithData } = useContactOverlay()
  const { getAllSections, getDocuments } = useHelpContent()

  const sections = getAllSections()
  const documents = getDocuments()
  
  // Get all items with videos for the playlist
  const allVideoItems = React.useMemo(() => {
    const items: Array<{ item: HelpItem; section: HelpSection }> = []
    sections.forEach(section => {
      section.items.forEach(item => {
        if (item.videoUrl) {
          items.push({ item, section })
        }
      })
    })
    return items
  }, [sections])

  const [selection, setSelection] = React.useState<HelpSelection | null>(() => {
    if (documents.length > 0) return { type: "documents" }
    if (allVideoItems.length > 0) {
      return { type: "video", item: allVideoItems[0].item, section: allVideoItems[0].section }
    }
    return null
  })

  // Function to mark user selection for auto-play
  const [hasUserSelected, setHasUserSelected] = React.useState(false)
  
  const markUserSelection = React.useCallback(() => {
    setHasUserSelected(true)
  }, [])

  // Reset hasUserSelected when overlay opens
  React.useEffect(() => {
    if (state === "open") {
      setHasUserSelected(false)
    }
  }, [state])

  // Video management state
  const videoRefs = React.useRef<Map<string, HTMLVideoElement>>(new Map())

  const stopOtherPlayers = React.useCallback((currentPlayerId: string) => {
    // Stop all other videos
    videoRefs.current.forEach((video, id) => {
      if (id !== currentPlayerId && !video.paused) {
        video.pause()
      }
    })
  }, [])

  const handleContactUs = () => {
    setState("closed")
    openWithData?.()
  }

  const selectedVideo = selection?.type === "video" ? selection : null
  const documentsSelected = selection?.type === "documents"

  return (
    <VideoPlayerContext.Provider value={{ stopOtherPlayers }}>
      <Dialog open={state === "open"} onOpenChange={(open) => setState(open ? "open" : "closed")}>
        <DialogContent className="w-full max-w-[1200px] max-h-[85vh] mx-auto !max-w-[1200px] sm:!max-w-[1200px] p-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-600" />
              Help & Support
            </DialogTitle>
            <DialogDescription>
              Find answers to common questions and get support for using the application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex h-[calc(85vh-120px)]">
            {/* Left Column - Playlist */}
            <div className="w-1/2 border-r overflow-y-auto">
              <div className="p-4 space-y-4">
                {documents.length > 0 && (
                  <DocumentsNavCard
                    isSelected={!!documentsSelected}
                    count={documents.length}
                    onSelect={() => setSelection({ type: "documents" })}
                  />
                )}

                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Video Tutorials</h3>
                  {allVideoItems.map(({ item, section }) => (
                    <HelpItemCard 
                      key={`${section.id}-${item.id}`}
                      item={item} 
                      section={section} 
                      isSelected={
                        selectedVideo?.item.id === item.id &&
                        selectedVideo?.section.id === section.id
                      }
                      onSelect={() => {
                        setSelection({ type: "video", item, section })
                        markUserSelection()
                      }}
                    />
                  ))}
                </div>
                
                {/* Contact Us Section */}
                <div className="pt-6 border-t">
                  <div className="text-center py-4">
                    <h3 className="text-lg font-semibold mb-2">Need More Help?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Can't find what you're looking for? Our support team is here to help.
                    </p>
                    <Button 
                      onClick={handleContactUs}
                      size="sm"
                      className="px-6"
                    >
                      Contact Us
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="w-1/2 p-4 flex flex-col overflow-y-auto">
              {documentsSelected ? (
                <DocumentsList documents={documents} />
              ) : selectedVideo ? (
                <EmbeddedVideoPlayer
                  videoUrl={selectedVideo.item.videoUrl!}
                  onClose={() => setState("closed")}
                  isOverlayOpen={state === "open"}
                  hasUserSelected={hasUserSelected}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a video from the playlist to get started</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </VideoPlayerContext.Provider>
  )
}

// Export all components and hooks
export { HelpOverlayProvider, HelpOverlayTrigger, HelpOverlay, useHelpOverlay }